import type React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { BarsArrowDownIcon } from "@heroicons/react/24/outline";

import { useSearchRanking } from "../SearchRanking/context";

export type SortDropdownProps = {
  sortByRelevanceLabel: string;
  sortByDistanceLabel: string;
  accessibilityLabel: string;
  // metrics events
  on?: (event: "openSortMenu" | "sortDistance" | "sortRelevance") => void;
};

export const SortDropdown: React.FC<SortDropdownProps> = ({
  sortByRelevanceLabel,
  sortByDistanceLabel,
  accessibilityLabel,
  on = () => {}, // default no-op
}) => {
  const searchRanking = useSearchRanking();
  if (!searchRanking) {
    throw new Error("SortDropdown must be used within a SearchRankingProvider");
  }
  const { sortMode, setSortMode, requestLocation, locationDenied } = searchRanking;

  const handleRelevanceClick = () => {
    setSortMode("relevance");
    on("sortRelevance");
  };

  const handleDistanceClick = async () => {
    if (locationDenied) return;

    try {
      await requestLocation();
      setSortMode("distance");
      on("sortDistance");
    } catch {
      // Location denied - state is updated by requestLocation
    }
  };

  return (
    <DropdownMenu.Root onOpenChange={(isOpen) => isOpen && on("openSortMenu")}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="sid-sort-dropdown__trigger"
          aria-label={accessibilityLabel}
        >
          <BarsArrowDownIcon className="sid-sort-dropdown__icon" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        className="sid-sort-dropdown__content"
        sideOffset={0}
        collisionPadding={10}
        align="end"
      >
        <DropdownMenu.Item
          className="sid-sort-dropdown__item"
          onSelect={handleRelevanceClick}
          data-active={sortMode === "relevance" ? "" : undefined}
        >
          {sortByRelevanceLabel}
        </DropdownMenu.Item>

        <DropdownMenu.Item
          className="sid-sort-dropdown__item"
          onSelect={handleDistanceClick}
          disabled={locationDenied}
          data-active={sortMode === "distance" ? "" : undefined}
        >
          {sortByDistanceLabel}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
